import React, { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { useWeather } from '../../context';
import { getStormIcon, ICON_URLS } from '../../utils/stormIcons';

export function StormReportsLayer({ map }: { map: mapboxgl.Map }) {
  const { 
    weatherData, 
    triggerValue, 
    triggerOperator,
    selectedReport,
    setSelectedReport
  } = useWeather();
  
  const popupRef = useRef<mapboxgl.Popup | null>(null);
  const loadedIconsRef = useRef<Set<string>>(new Set());
  const layerAddedRef = useRef(false);
  const lastDataRef = useRef<string>('');

  // Load icons when map loads
  useEffect(() => {
    if (!map) return;

    const loadIcons = async () => {
      for (const [id, url] of Object.entries(ICON_URLS)) {
        if (!map.hasImage(id) && !loadedIconsRef.current.has(id)) {
          try {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            
            await new Promise((resolve, reject) => {
              img.onload = resolve;
              img.onerror = reject;
              img.src = url;
            });

            if (map.hasImage(id)) continue;
            
            map.addImage(id, img);
            loadedIconsRef.current.add(id);
          } catch (error) {
            console.error(`Failed to load icon ${id}:`, error);
          }
        }
      }
    };

    loadIcons();
  }, [map]);

  useEffect(() => {
    if (!map || !weatherData || !Array.isArray(weatherData)) return;

    // Create a hash of the current data to compare
    const currentDataHash = JSON.stringify(weatherData);
    if (currentDataHash === lastDataRef.current) {
      return; // Skip if data hasn't changed
    }
    lastDataRef.current = currentDataHash;

    const cleanup = () => {
      if (map.getStyle() && layerAddedRef.current) {
        if (map.getLayer('stormreports-icons')) {
          map.removeLayer('stormreports-icons');
        }
        if (map.getLayer('stormreports-layer')) {
          map.removeLayer('stormreports-layer');
        }
        if (map.getSource('stormreports-data')) {
          map.removeSource('stormreports-data');
        }
        layerAddedRef.current = false;
      }
      if (popupRef.current) {
        popupRef.current.remove();
      }
    };

    try {
      cleanup();

      const features = weatherData
        .map(report => {
          const longitude = report.loc?.long;
          const latitude = report.loc?.lat;
          
          if (!longitude || !latitude) return null;

          let radius = 6;
          let value = 0;
          let measurementType = '';
          let unit = 'inches';
          let shouldUseCircle = false;

          // Handle different types of measurements
          const detail = report.report?.detail;
          const category = report.report?.cat?.toLowerCase();

          switch (category) {
            case 'blizzard':
              const snowMatch = report.report?.comments?.match(/(\d+\.?\d*)\s*inches?/i);
              value = snowMatch ? parseFloat(snowMatch[1]) : 0;
              measurementType = 'blizzard';
              if (!isNaN(value) && value > 0) {
                radius = 6 + (value * 3);
              }
              shouldUseCircle = true;
              break;

            case 'hail':
              value = detail?.hailIN ? parseFloat(detail.hailIN) : 0;
              measurementType = 'hail';
              if (!isNaN(value) && value > 0) {
                radius = 6 + (value * 10);
              }
              break;

            case 'ice':
            case 'rain':
              value = detail?.rainIN ? parseFloat(detail.rainIN) : 0;
              measurementType = category;
              unit = 'inches';
              if (!isNaN(value) && value > 0) {
                radius = 6 + (value * (category === 'ice' ? 20 : 5));
              }
              break;

            case 'snow':
              value = detail?.snowIN ? parseFloat(detail.snowIN) : 0;
              measurementType = 'snow';
              unit = 'inches';
              if (!isNaN(value) && value > 0) {
                radius = 6 + (value * 3);
              }
              break;

            case 'wind':
              value = detail?.windSpeedMPH ? parseFloat(detail.windSpeedMPH) : 0;
              measurementType = 'wind';
              unit = 'mph';
              if (!isNaN(value) && value > 0) {
                radius = 6 + (value / 5);
              }
              break;

            case 'flood':
              let floodDepth = 0;
              const comments = report.report?.comments || '';
              const detailText = report.report?.detail?.text || '';
              
              const feetMatch = (comments + ' ' + detailText).match(/(\d+\.?\d*)\s*(?:feet|foot|ft)/i);
              if (feetMatch) {
                floodDepth = parseFloat(feetMatch[1]);
              } else {
                floodDepth = 1;
              }
              
              value = floodDepth;
              measurementType = 'flood';
              unit = 'feet';
              radius = 6 + (value * 5);
              shouldUseCircle = true;
              break;

            default:
              measurementType = category || '';
              if (detail?.text) {
                value = parseFloat(detail.text);
                if (!isNaN(value)) {
                  radius = 6 + (value * 5);
                }
              }
          }

          const isSelected = selectedReport?.id === report.id;
          if (value > 0) shouldUseCircle = true;

          return {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [longitude, latitude]
            },
            properties: {
              id: report.id,
              type: report.report?.type,
              cat: report.report?.cat,
              name: report.report?.name,
              value,
              unit,
              measurementType,
              radius,
              timestampISO: report.report?.dateTimeISO || report.report?.datetime,
              reporter: report.report?.reporter,
              comments: report.report?.comments,
              detailText: report.report?.detail?.text,
              locationName: report.locationName,
              locationIndex: report.locationIndex,
              locationColor: report.locationColor,
              isSelected,
              icon: getStormIcon(report.report?.cat),
              useCircle: shouldUseCircle
            }
          };
        })
        .filter(f => f !== null);

      map.addSource('stormreports-data', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features
        }
      });

      map.addLayer({
        id: 'stormreports-layer',
        type: 'circle',
        source: 'stormreports-data',
        filter: ['==', ['get', 'useCircle'], true],
        paint: {
          'circle-radius': ['get', 'radius'],
          'circle-color': [
            'case',
            ['get', 'isSelected'],
            '#800080',
            [
              'case',
              [
                'match',
                ['literal', triggerOperator],
                '>', ['>', ['get', 'value'], triggerValue],
                '>=', ['>=', ['get', 'value'], triggerValue],
                '<', ['<', ['get', 'value'], triggerValue],
                '<=', ['<=', ['get', 'value'], triggerValue],
                '=', ['==', ['get', 'value'], triggerValue],
                false
              ],
              '#FF0000',
              [
                'match',
                ['get', 'measurementType'],
                'rain', '#0000FF',
                'ice', '#00FFFF',
                'snow', '#FFFFFF',
                'hail', '#FF00FF',
                'wind', '#FFA500',
                'flood', '#4B0082',
                'blizzard', '#87CEEB',
                'tornado', '#8B0000',
                ['get', 'locationColor']
              ]
            ]
          ],
          'circle-stroke-width': 1,
          'circle-stroke-color': '#000',
          'circle-opacity': 0.7
        }
      });

      map.addLayer({
        id: 'stormreports-icons',
        type: 'symbol',
        source: 'stormreports-data',
        filter: ['==', ['get', 'useCircle'], false],
        layout: {
          'icon-image': ['get', 'icon'],
          'icon-size': 0.5,
          'icon-allow-overlap': true
        }
      });

      layerAddedRef.current = true;

      popupRef.current = new mapboxgl.Popup({
        closeButton: true,
        closeOnClick: false,
        className: 'weather-data-popup-container'
      });

      const handleClick = (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] }) => {
        if (!e.features?.length) return;
        
        const feature = e.features[0];
        const reportId = feature.properties?.id;
        const clickedReport = weatherData.find(r => r.id === reportId);
        
        if (clickedReport) {
          setSelectedReport(selectedReport?.id === reportId ? null : clickedReport);

          if (popupRef.current) {
            const props = feature.properties;
            const measurementLabel = {
              hail: 'Hail Size',
              ice: 'Ice Accumulation',
              rain: 'Rainfall',
              snow: 'Snowfall',
              wind: 'Wind Speed',
              flood: 'Flood Depth',
              blizzard: 'Snow Accumulation',
              tornado: 'Rating'
            }[props.measurementType] || 'Measurement';
            
            const html = `
              <div class="p-4">
                <h4 class="text-lg font-bold mb-2">${props.name || 'Storm Report'}</h4>
                <div class="space-y-2 text-sm">
                  <p><span class="text-[var(--secondary-text)]">Type:</span> ${props.type}</p>
                  <p><span class="text-[var(--secondary-text)]">Category:</span> ${props.cat}</p>
                  <p><span class="text-[var(--secondary-text)]">Time:</span> ${new Date(props.timestampISO).toLocaleString()}</p>
                  ${props.value ? `<p><span class="text-[var(--secondary-text)]">${measurementLabel}:</span> ${props.value} ${props.unit}</p>` : ''}
                  ${props.detailText ? `<p><span class="text-[var(--secondary-text)]">Details:</span> ${props.detailText}</p>` : ''}
                  ${props.comments ? `<p><span class="text-[var(--secondary-text)]">Comments:</span> ${props.comments}</p>` : ''}
                  ${props.reporter ? `<p><span class="text-[var(--secondary-text)]">Reporter:</span> ${props.reporter}</p>` : ''}
                </div>
              </div>
            `;

            popupRef.current
              .setLngLat(e.lngLat)
              .setHTML(html)
              .addTo(map);
          }
        }
      };

      map.on('click', 'stormreports-layer', handleClick);
      map.on('click', 'stormreports-icons', handleClick);
      
      map.on('mouseenter', 'stormreports-layer', () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseenter', 'stormreports-icons', () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      
      map.on('mouseleave', 'stormreports-layer', () => {
        map.getCanvas().style.cursor = '';
      });
      map.on('mouseleave', 'stormreports-icons', () => {
        map.getCanvas().style.cursor = '';
      });
    } catch (error) {
      console.error('Error rendering storm reports:', error);
      cleanup();
    }

    return cleanup;
  }, [map, weatherData, triggerValue, triggerOperator, selectedReport, setSelectedReport]);

  return null;
}